/**
 * The camera of the scan, ported from WR Scanner (CameraCapture.jsx, brief
 * section 10): the guide's shape for each subject, and the crop that sends
 * exactly what is inside the red frame.
 */
export const SUBJECTS = [
  { id: 'card', label: 'Card', widthPct: 0.88, aspect: 1.7 },
  { id: 'badge', label: 'Badge', widthPct: 0.64, aspect: 0.72 },
  { id: 'note', label: 'Note', widthPct: 0.84, aspect: 0.78 },
] as const;
export type SubjectId = (typeof SUBJECTS)[number]['id'];

const CROP_MARGIN = 0.06;

/** The guide's size in the viewfinder, as the prototype draws it. */
export function guideStyle(subject: (typeof SUBJECTS)[number]) {
  return {
    width: `min(${subject.widthPct * 100}cqw, ${(78 * subject.aspect).toFixed(1)}cqh)`,
    aspectRatio: String(subject.aspect),
  };
}

export interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * The part of the camera frame inside the guide, with a small margin. The
 * video is drawn with object-fit: cover, so the preview is a centred crop of
 * the real frame: this reverses that transform (WR Scanner, verbatim).
 */
export function cropFromGuide(video: Box, guide: Box, vw: number, vh: number) {
  const scale = Math.max(video.width / vw, video.height / vh);
  const offsetX = (video.width - vw * scale) / 2;
  const offsetY = (video.height - vh * scale) / 2;
  const toFrame = (cx: number, cy: number) => ({
    x: (cx - video.left - offsetX) / scale,
    y: (cy - video.top - offsetY) / scale,
  });
  const tl = toFrame(guide.left, guide.top);
  const br = toFrame(guide.left + guide.width, guide.top + guide.height);
  const mx = (br.x - tl.x) * CROP_MARGIN;
  const my = (br.y - tl.y) * CROP_MARGIN;
  const sx = Math.max(0, Math.round(tl.x - mx));
  const sy = Math.max(0, Math.round(tl.y - my));
  const sw = Math.min(vw - sx, Math.round(br.x - tl.x + 2 * mx));
  const sh = Math.min(vh - sy, Math.round(br.y - tl.y + 2 * my));
  if (sw < 40 || sh < 40) return null;
  return { sx, sy, sw, sh };
}

/** Quality 0.95: the only lossy step before the worker (WR Scanner). */
const JPEG_QUALITY = 0.95;

function toJpeg(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('No image'))),
      'image/jpeg',
      JPEG_QUALITY,
    ),
  );
}

/** What is inside the guide, at the camera's full resolution: no downscale here. */
export async function capture(video: HTMLVideoElement, guide: HTMLElement): Promise<Blob | null> {
  if (!video.videoWidth) return null;
  const crop = cropFromGuide(
    video.getBoundingClientRect(),
    guide.getBoundingClientRect(),
    video.videoWidth,
    video.videoHeight,
  );
  if (!crop) return null;
  const canvas = document.createElement('canvas');
  canvas.width = crop.sw;
  canvas.height = crop.sh;
  canvas
    .getContext('2d')!
    .drawImage(video, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, crop.sw, crop.sh);
  return toJpeg(canvas);
}

/** A chosen photo is turned upright and kept under this size (several times what the reading uses). */
const MAX_EDGE = 3000;

/** A photo from the gallery, as a JPEG the worker reads (iPhone HEIC photos included). */
export async function fromFile(file: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  return toJpeg(canvas);
}

/** The rear camera, as much resolution as the device gives. */
export function openCamera(): Promise<MediaStream> {
  if (!navigator.mediaDevices?.getUserMedia) return Promise.reject(new Error('No camera'));
  return navigator.mediaDevices.getUserMedia({
    video: {
      facingMode: { ideal: 'environment' },
      width: { ideal: 3840 },
      height: { ideal: 2160 },
    },
    audio: false,
  });
}
